import React from 'react';
import { Check, Plus } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { Modal } from 'src/components/Modal';
import { cn } from 'src/utils/className';

type AddInsightChoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  onSelectExisting: () => void;
};

enum AddInsightChoice {
  CREATE_NEW_INSIGHT = 'CREATE_NEW_INSIGHT',
  ADD_EXISTING_INSIGHTS = 'ADD_EXISTING_INSIGHTS',
}

const choices = [
  {
    key: AddInsightChoice.CREATE_NEW_INSIGHT,
    icon: Plus,
    title: 'Create new insight',
    description: 'Specify the description, type, and instructions.',
    iconBg: 'bg-primary-100 text-primary',
  },
  {
    key: AddInsightChoice.ADD_EXISTING_INSIGHTS,
    icon: Check,
    title: 'Add existing insights',
    description: 'Reuse existing insights.',
    iconBg: 'bg-neutral-100 text-neutral-600',
  },
] as const;

export const AddInsightChoiceModal: React.FC<AddInsightChoiceModalProps> = ({
  isOpen,
  onClose,
  onCreateNew,
  onSelectExisting,
}) => {
  const handleClick = (key: AddInsightChoice) => {
    if (key === AddInsightChoice.CREATE_NEW_INSIGHT) {
      onCreateNew();
    } else {
      onSelectExisting();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Call Insight"
      subtitle="Specify what you want to learn from each call."
      showClose
      showModalBorder
      className="lg:max-w-xl"
      footer={
        <div className="flex justify-end gap-2 pt-4 px-6 pb-6 border-t border-neutral-200">
          <Button variant="outlined" onClick={onClose} size="small">
            Cancel
          </Button>
        </div>
      }
    >
      <div className="py-6 space-y-3">
        <div className="text-sm font-medium text-neutral-600">
          How would you like to add an insight?
        </div>
        <div className="grid grid-cols-2 gap-3">
          {choices.map((choice) => (
            <Button
              key={choice.key}
              type="button"
              variant="outlined"
              className={cn(
                'w-full justify-start rounded-lg border border-neutral-200 bg-neutral-0 p-4 text-left',
                'hover:border-primary-300 hover:shadow-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200',
              )}
              onClick={() => handleClick(choice.key)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg',
                    choice.iconBg,
                  )}
                >
                  <choice.icon className="h-5 w-5" weight="bold" />
                </div>
                <div>
                  <div className="font-medium text-sm text-neutral-700">
                    {choice.title}
                  </div>
                  <div className="text-xs text-neutral-400 mt-0.5">
                    {choice.description}
                  </div>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </div>
    </Modal>
  );
};
